// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/ReceptionCard.tsx
// 🔧 PURPOSE: La reception in fondo alla Home, sulla card espresso delle
//             email (stone-900 in scuro):
//             - full (soggiorno e dopo): lo stemma, "Siamo qui per te, 24
//               ore su 24" e i due bottoni già divisi: "Scrivi alla
//               reception" (una persona, con il pallino dei non letti) e
//               "Assistente AI · risposta immediata";
//             - ask (prima dell'arrivo): "Domande sull'arrivo? Reception in
//               chat 24 ore su 24" e SCRIVICI, che apre la scelta
//               Assistente AI / reception.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { useHomeSession } from './HomeSessionHost';
import { Emblem } from './HomeMasthead';
import { GoldButton, GoldRule, LineOnDarkButton, Section, UnreadBadge } from './ui';

export type ReceptionCardVariant = 'full' | 'ask';

export interface ReceptionCardProps {
  variant?: ReceptionCardVariant;
  className?: string;
}

const CARD = 'rounded-[20px] bg-nva-espresso dark:border dark:border-stone-800 dark:bg-stone-900';
/** Il bottone oro sull'espresso: l'anello del fuoco avorio, staccato dal bordo (blu e oro non si vedrebbero). */
const GOLD_ON_CARD_FOCUS =
  'focus-visible:ring-nva-bone dark:focus-visible:ring-nva-bone focus-visible:ring-offset-2 focus-visible:ring-offset-nva-espresso dark:focus-visible:ring-offset-stone-900';

const ReceptionCard: React.FC<ReceptionCardProps> = ({ variant = 'full', className }) => {
  const { t } = useTranslation();
  const { openChat, openAssistant, openReception, unreadCount } = useHomeSession();
  const titleId = `home-reception-title-${variant}`;
  const unreadLabel = t('home.reception.unread', { count: unreadCount });

  if (variant === 'ask') {
    return (
      <Section labelledBy={titleId} className={className}>
        <div className={cn(CARD, 'flex items-center gap-3 px-4 py-[18px]')}>
          <div className="flex min-w-0 flex-grow flex-col gap-[3px]">
            <h2 id={titleId} className="m-0 font-display text-[18px] font-bold leading-snug text-nva-bone">
              {t('home.before.askTitle')}
            </h2>
            <p className="m-0 text-[13px] leading-snug text-nva-foot">{t('home.before.askDesc')}</p>
          </div>
          <GoldButton onClick={openReception} className={cn('relative flex-shrink-0', GOLD_ON_CARD_FOCUS)}>
            {t('home.quick.write')}
            <UnreadBadge count={unreadCount} label={unreadLabel} className="absolute -right-1.5 -top-1.5 border-nva-espresso dark:border-stone-900" />
          </GoldButton>
        </div>
      </Section>
    );
  }

  return (
    <Section labelledBy={titleId} className={className}>
      <div className={cn(CARD, 'flex flex-col items-center gap-2 px-4 py-5 text-center')}>
        <Emblem size={56} />
        <GoldRule className="my-1" />
        <h2 id={titleId} className="m-0 font-display text-[21px] font-bold leading-tight text-nva-bone">
          {t('home.reception.title')}
        </h2>
        <p className="m-0 text-[13.5px] leading-snug text-nva-foot">{t('home.reception.body')}</p>
        <GoldButton full onClick={openChat} className={cn('relative mt-1.5', GOLD_ON_CARD_FOCUS)}>
          {t('home.reception.cta')}
          {unreadCount > 0 ? <UnreadBadge count={unreadCount} label={unreadLabel} className="ml-1 border-0" /> : null}
        </GoldButton>
        <LineOnDarkButton full onClick={openAssistant}>
          {t('home.reception.ai')}
        </LineOnDarkButton>
      </div>
    </Section>
  );
};

export default ReceptionCard;
